/**
 * Live-rates WebSocket client — the streaming counterpart to the /rates REST call.
 *
 * Connects to `/api/ws/rates/` (same origin, so wss:// in prod), keeps the latest
 * board in state as the server ticks it, and exposes `requestQuote()` which sends
 * an instant conversion request over the SAME socket and resolves with the quote
 * (receive amount + the itemised charge). One socket feeds both the live prices and
 * the simulation, so what the screen shows and what settles are the same number.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface LiveRate {
  asset: string;
  kes_per_unit: string;
}

export interface RateBoardLive {
  numeraire: string;
  source: string;
  as_of: string;
  live: boolean;
  rates: LiveRate[];
}

export interface LiveQuote {
  from: string;
  to: string;
  pay: string;
  pay_asset: string;
  rate: string;
  fee: string;
  fee_asset: string;
  gross_receive: string;
  receive: string;
  receive_asset: string;
}

type Status = 'connecting' | 'live' | 'closed';

export function useLiveRates() {
  const [board, setBoard] = useState<RateBoardLive | null>(null);
  const [status, setStatus] = useState<Status>('connecting');
  const wsRef = useRef<WebSocket | null>(null);
  const pending = useRef<((q: LiveQuote) => void)[]>([]);
  const retry = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let closed = false;

    const connect = () => {
      if (closed) return;
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${proto}//${window.location.host}/api/ws/rates/`);
      wsRef.current = ws;
      setStatus('connecting');

      ws.onopen = () => setStatus('live');
      ws.onclose = () => {
        setStatus('closed');
        if (!closed) retry.current = setTimeout(connect, 3000); // auto-reconnect
      };
      ws.onerror = () => ws.close();
      ws.onmessage = (e) => {
        try {
          const m = JSON.parse(e.data);
          if (m.type === 'board') setBoard(m as RateBoardLive);
          else if (m.type === 'quote') pending.current.shift()?.(m as LiveQuote);
        } catch {
          /* ignore malformed frames */
        }
      };
    };

    connect();
    return () => {
      closed = true;
      if (retry.current) clearTimeout(retry.current);
      wsRef.current?.close();
    };
  }, []);

  const requestQuote = useCallback((from: string, to: string, amount: string) => {
    return new Promise<LiveQuote | null>((resolve) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return resolve(null);
      pending.current.push(resolve as (q: LiveQuote) => void);
      ws.send(JSON.stringify({ action: 'quote', from, to, amount }));
    });
  }, []);

  return { board, status, requestQuote };
}
